import { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Badge, Button, Modal } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { getTeacherGroups, getAvailableTeachers, assignGroupSubstitute, removeGroupSubstitute, getGroupStudents } from '../../../actions/classActions'
import Loader from '../../../components/loaders/Loader'
import Swal from 'sweetalert2'
import { toastError } from '../../../components/swal/toast'
import Select from 'react-select'

export default function GroupList() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { teacherGroups, loading, availableTeachers, groupStudents } = useSelector(state => state.classes)
    const [showSubstituteModal, setShowSubstituteModal] = useState(false)
    const [showStudentsModal, setShowStudentsModal] = useState(false)
    const [selectedGroup, setSelectedGroup] = useState(null)
    const [selectedTeacher, setSelectedTeacher] = useState(null)
    const [loadingStudents, setLoadingStudents] = useState(false)

    useEffect(() => {
        dispatch(getTeacherGroups())
    }, [dispatch])

    if (loading && (!teacherGroups || teacherGroups.length === 0)) return <Loader />

    const handleOpenSubstituteModal = (group) => {
        setSelectedGroup(group)
        setSelectedTeacher(null)
        dispatch(getAvailableTeachers())
        setShowSubstituteModal(true)
    }

    const handleOpenStudentsModal = async (group) => {
        setSelectedGroup(group)
        setLoadingStudents(true)
        setShowStudentsModal(true)
        await dispatch(getGroupStudents(group.odooGroupId))
        setLoadingStudents(false)
    }

    const handleAssignSubstitute = async () => {
        if (!selectedTeacher) {
            toastError(t('teacher.groups.selectTeacher'))
            return
        }
        const result = await dispatch(assignGroupSubstitute(
            selectedGroup.odooGroupId,
            selectedGroup.name,
            selectedTeacher.teacher.id,
            selectedTeacher.teacher.name
        ))
        if (result) {
            setShowSubstituteModal(false)
            setSelectedGroup(null)
            setSelectedTeacher(null)
        }
    }

    const handleRemoveSubstitute = async (group) => {
        const result = await Swal.fire({
            title: t('teacher.groups.removeSubstituteConfirm'),
            text: t('teacher.groups.removeSubstituteText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('global.yes'),
            cancelButtonText: t('global.cancel')
        })
        if (result.isConfirmed) {
            await dispatch(removeGroupSubstitute(group.odooGroupId))
        }
    }

    const teacherOptions = availableTeachers.map(teacher => ({
        value: teacher.id,
        label: `${teacher.name} (${teacher.email})`,
        teacher
    }))

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col>
                    <h3 className='fw-bold mb-1'>{t('teacher.groups.title')}</h3>
                    <p className='text-muted mb-4'>{teacherGroups.length} {t('teacher.groups.subtitle')}</p>
                </Col>
            </Row>

            <Row className='g-3'>
                {teacherGroups.length > 0
                    ? teacherGroups.map(group => (
                        <Col key={group.odooGroupId} xs={12} md={6} xl={4}>
                            <Card className='border-0 shadow-sm rounded-4 h-100'>
                                <Card.Body className='p-4'>
                                    <div className='d-flex justify-content-between align-items-start mb-2'>
                                        <div>
                                            <h5 className='fw-bold mb-1'>{group.name}</h5>
                                            <p className='text-muted mb-0' style={{ fontSize: '13px' }}>{group.courseName}</p>
                                        </div>
                                        {group.substitute && (
                                            <Badge bg='info' className='rounded-pill'>{t('teacher.groups.substitute')}</Badge>
                                        )}
                                    </div>

                                    <div className='mb-3'>
                                        <p className='mb-1' style={{ fontSize: '14px', color: '#374151' }}>
                                            <strong>{group.modality || t('teacher.groups.nomodality')}</strong>
                                        </p>
                                        <p className='mb-0' style={{ fontSize: '14px', color: '#6b7280' }}>
                                            {group.studentCount || 0} {t('teacher.groups.students')}
                                        </p>
                                    </div>

                                    {group.substitute && (
                                        <div className='mb-3 p-2 bg-info bg-opacity-10 rounded-3'>
                                            <p className='mb-0' style={{ fontSize: '13px', color: '#0c4a6e' }}>
                                                <strong>{t('teacher.groups.assignedSubstitute')}:</strong> {group.substitute.name}
                                            </p>
                                        </div>
                                    )}

                                    <div className='d-flex gap-2'>
                                        <Button
                                            variant='primary'
                                            size='sm'
                                            className='rounded-3'
                                            onClick={() => handleOpenStudentsModal(group)}
                                        >
                                            {t('teacher.groups.viewStudents')}
                                        </Button>
                                        {!group.substitute ? (
                                            <Button
                                                variant='outline-warning'
                                                size='sm'
                                                className='rounded-3 flex-grow-1'
                                                onClick={() => handleOpenSubstituteModal(group)}
                                            >
                                                {t('teacher.groups.assignSubstitute')}
                                            </Button>
                                        ) : (
                                            <Button
                                                variant='outline-danger'
                                                size='sm'
                                                className='rounded-3 flex-grow-1'
                                                onClick={() => handleRemoveSubstitute(group)}
                                            >
                                                {t('teacher.groups.removeSubstitute')}
                                            </Button>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))
                    : <Col><p className='text-muted'>{t('teacher.groups.nogroups')}</p></Col>
                }
            </Row>

            {/* Modal de Alumnos */}
            <Modal show={showStudentsModal} onHide={() => setShowStudentsModal(false)} centered contentClassName='rounded-4'>
                <Modal.Header closeButton>
                    <Modal.Title className='fs-6 fw-bold'>{t('teacher.groups.students')} — {selectedGroup?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ width: '400px', minHeight: '120px' }}>
                    {loadingStudents ? (
                        <div className='text-center py-4'>
                            <Loader />
                        </div>
                    ) : groupStudents && groupStudents.length > 0 ? (
                        <div>
                            <p className='text-muted mb-2' style={{ fontSize: '12px' }}>
                                {groupStudents.length} {t('teacher.groups.students')}
                            </p>
                            {groupStudents.map(student => (
                                <div key={student.id} className='d-flex align-items-center justify-content-between p-2 mb-1 bg-light rounded-3'>
                                    <span className='fw-semibold' style={{ fontSize: '12px' }}>{student.name}</span>
                                    <span className='text-muted' style={{ fontSize: '11px' }}>{student.courseName}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className='text-muted text-center py-4' style={{ fontSize: '12px' }}>{t('teacher.groups.nostudents')}</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' size='sm' className='rounded-3' onClick={() => setShowStudentsModal(false)}>
                        {t('global.close')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Asignar Sustituto */}
            <Modal show={showSubstituteModal} onHide={() => setShowSubstituteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('teacher.groups.assignSubstitute')} - {selectedGroup?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className='text-muted mb-3'>{t('teacher.groups.selectSubstituteTeacher')}</p>
                    <Select
                        options={teacherOptions}
                        value={selectedTeacher}
                        onChange={(option) => setSelectedTeacher(option)}
                        placeholder={t('teacher.groups.selectTeacherPlaceholder')}
                        isClearable
                        className='mb-2'
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' className='rounded-3' onClick={() => setShowSubstituteModal(false)}>
                        {t('global.cancel')}
                    </Button>
                    <Button variant='primary' className='rounded-3' onClick={handleAssignSubstitute}>
                        {t('global.register')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}
